/**
 * Typed WebSocket incoming event definitions for the fund-compliance agent.
 *
 * These types document what the backend sends. Fields are optional where the
 * backend may or may not include them. The `type` discriminator is always present.
 */

import type { JsonRecord } from "@/lib/fund-compliance/parse-primitives";

export interface WsConnectedEvent {
  type: "connected";
  chatId?: string;
  authenticated?: boolean;
  user?: JsonRecord;
}

export interface WsHistoryEvent {
  type: "history";
  chatId?: string;
  messages: unknown[];
  hasMore?: boolean;
  nextBefore?: string;
}

export interface WsUserMessageEvent {
  type: "user_message";
  chatId?: string;
  id?: string;
  role?: string;
  content?: unknown;
  metadata?: unknown;
  attachments?: unknown[];
  [key: string]: unknown;
}

export interface WsAssistantDeltaEvent {
  type: "assistant_delta";
  chatId?: string;
  delta?: string;
  content?: unknown;
  streamId?: string;
}

export interface WsAssistantMessageEvent {
  type: "assistant_message";
  chatId?: string;
  streamId?: string;
  [key: string]: unknown;
}

export interface WsAssistantDoneEvent {
  type: "assistant_done";
  chatId?: string;
  streamId?: string;
}

export interface WsProgressEvent {
  type: "queue_status" | "review_progress" | "review_job";
  chatId?: string;
  [key: string]: unknown;
}

export interface WsReportReadyEvent {
  type: "report_ready";
  chatId?: string;
  [key: string]: unknown;
}

export interface WsToolUseEvent {
  type: "tool_use";
  chatId?: string;
  toolId?: string;
  toolName?: string;
  toolInput?: JsonRecord;
}

export interface WsResultEvent {
  type: "result";
  chatId?: string;
  success?: boolean;
  [key: string]: unknown;
}

export interface WsErrorEvent {
  type: "error" | "unauthorized";
  chatId?: string;
  code?: string;
  [key: string]: unknown;
}

export type WsIncomingEvent =
  | WsConnectedEvent
  | WsHistoryEvent
  | WsUserMessageEvent
  | WsAssistantDeltaEvent
  | WsAssistantMessageEvent
  | WsAssistantDoneEvent
  | WsProgressEvent
  | WsReportReadyEvent
  | WsToolUseEvent
  | WsResultEvent
  | WsErrorEvent;

const KNOWN_EVENT_TYPES = new Set<string>([
  "connected",
  "history",
  "user_message",
  "assistant_delta",
  "assistant_message",
  "assistant_done",
  "queue_status",
  "review_progress",
  "review_job",
  "report_ready",
  "tool_use",
  "result",
  "error",
  "unauthorized",
]);

export function parseWsEvent(data: string): WsIncomingEvent | null {
  let payload: JsonRecord;
  try {
    payload = JSON.parse(data) as JsonRecord;
  } catch {
    return null;
  }

  const eventType = typeof payload.type === "string" ? payload.type : undefined;
  if (!eventType || !KNOWN_EVENT_TYPES.has(eventType)) return null;

  return payload as unknown as WsIncomingEvent;
}
